import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useLanguage } from '../../i18n';
import { useAuth } from '../../auth';
import { suggestTestcases, getTestCaseCategories, createTestCaseCategory, saveTestCasesBulk, getSavedTestCases, deleteSavedTestCase, type TestCaseCategory, type SaveTestCaseRequest, type SavedTestCaseResponse } from '../../api';
import type { SuggestedTestCase, TestCaseStatus } from '../../types';

interface SuggestedTestcasesSectionProps {
  featureName: string;
  planningIntent: string;
  risks: string;
  suggestedTestcases: SuggestedTestCase[];
  setSuggestedTestcases: (testcases: SuggestedTestCase[]) => void;
  isDemo?: boolean;
  qaCore?: string;  // core_scenario 선택 여부 ('pass' | 'fail' | 'n/a' | 'n/t' | '')
  qaEdge?: string;  // edge_cases 선택 여부
  onGeneratingChange?: (isGenerating: boolean) => void;  // 생성 중 상태 콜백
  githubSummary?: {
    repoFullName: string;
    commits: { sha: string; message: string; author?: string | null; date?: string | null; html_url?: string | null }[];
    merged: {
      focus_areas: { title: string; severity: 'high' | 'medium' | 'low'; reason: string; related_files: string[] }[];
      side_effects: string[];
      suggested_focus_areas: string[];
    };
  };
}

// 상태별 스타일 설정
const statusConfig: Record<TestCaseStatus, { bg: string; text: string; label: string }> = {
  'pass': { bg: 'bg-status-go/20', text: 'text-status-go', label: 'Pass' },
  'fail': { bg: 'bg-status-danger/20', text: 'text-status-danger', label: 'Fail' },
  'n/a': { bg: 'bg-terminal-muted/20', text: 'text-terminal-muted', label: 'N/A' },
  'n/t': { bg: 'bg-terminal-input', text: 'text-terminal-dim', label: 'N/T' },
};

function SuggestedTestcasesSection({
  featureName,
  planningIntent,
  risks,
  suggestedTestcases,
  setSuggestedTestcases,
  isDemo = false,
  qaCore = '',
  qaEdge = '',
  githubSummary,
  onGeneratingChange,
}: SuggestedTestcasesSectionProps) {
  const { lang, t } = useLanguage();
  const { user, quota } = useAuth();
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // 플랜별 AI 생성 접근 권한 확인
  const userPlan = quota?.plan || 'free';
  const canUseAIGenerate = useMemo(() => {
    // Max/Team 플랜만 AI 생성 가능
    return userPlan === 'max' || userPlan === 'team';
  }, [userPlan]);
  
  
  // 저장 관련 상태
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [categories, setCategories] = useState<TestCaseCategory[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | undefined>();
  const [newCategoryName, setNewCategoryName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  // 불러오기 관련 상태
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [savedTestcases, setSavedTestcases] = useState<SavedTestCaseResponse[]>([]);
  const [loadingCategory, setLoadingCategory] = useState<number | undefined>();
  const [isLoadingSaved, setIsLoadingSaved] = useState(false);
  
  // 기능 태그 관련 상태
  const [selectedTags, setSelectedTags] = useState<string[]>(featureName ? [featureName] : []);
  const [tagInput, setTagInput] = useState('');
  const [existingTags, setExistingTags] = useState<string[]>([]);
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);
  const [loadFeatureFilter, setLoadFeatureFilter] = useState<string | undefined>();
  const MAX_TAGS = 3;
  
  // 드래그 앤 드롭 상태
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOverCategory, setDragOverCategory] = useState<'core' | 'edge' | null>(null);
  
  // 카테고리 로드 (저장/불러오기 모달 둘 다)
  useEffect(() => {
    if (user && (showSaveModal || showLoadModal)) {
      getTestCaseCategories()
        .then(setCategories)
        .catch(console.error);
    }
  }, [user, showSaveModal, showLoadModal]);
  
  // 저장된 테스트케이스 로드
  useEffect(() => {
    if (user && showLoadModal) {
      setIsLoadingSaved(true);
      getSavedTestCases(loadingCategory)
        .then(data => {
          setSavedTestcases(data);
          // 기능 태그 수집 (쉼표로 구분된 경우 개별 태그로 분리)
          const tags = new Set<string>();
          data.forEach(tc => {
            if (tc.feature_name) {
              // 쉼표로 구분된 태그들을 분리
              tc.feature_name.split(',').forEach(tag => {
                const trimmed = tag.trim();
                if (trimmed) tags.add(trimmed);
              });
            }
          });
          setExistingTags(Array.from(tags).sort());
        })
        .catch(console.error)
        .finally(() => setIsLoadingSaved(false));
    }
  }, [user, showLoadModal, loadingCategory]);
  
  // 기능 태그 수집 (저장 모달 열 때)
  useEffect(() => {
    if (user && showSaveModal) {
      // 모든 저장된 테스트케이스에서 feature_name 수집 (쉼표로 구분된 경우 개별 태그로 분리)
      getSavedTestCases()
        .then(testcases => {
          const tags = new Set<string>();
          testcases.forEach(tc => {
            if (tc.feature_name) {
              tc.feature_name.split(',').forEach(tag => {
                const trimmed = tag.trim();
                if (trimmed) tags.add(trimmed);
              });
            }
          });
          setExistingTags(Array.from(tags).sort());
        })
        .catch(console.error);
      
      // 현재 기능명으로 초기화 (배열로)
      setSelectedTags(featureName ? [featureName] : []);
      setTagInput('');
    }
  }, [user, showSaveModal, featureName]);
  
  // 생성 상태 변경 시 부모에게 알림
  const updateIsGenerating = (value: boolean) => {
    setIsGenerating(value);
    onGeneratingChange?.(value);
  };

  // 테스트 케이스 생성
  const handleGenerate = useCallback(async () => {
    if (!user || isDemo) return;
    
    updateIsGenerating(true);
    setError(null);
    
    try {
      const planningItemsBase = planningIntent.split('\n').filter((line) => line.trim());
      const riskItemsBase = risks.split('\n').filter((line) => line.trim());

      // GitHub 커밋 분석을 "추가 컨텍스트"로 반영
      const githubPlanning = githubSummary?.merged?.focus_areas?.map((f) => `[GitHub] ${f.title}`) ?? [];
      const githubRisks = githubSummary?.merged?.side_effects?.map((s) => `[GitHub SideEffect] ${s}`) ?? [];

      const planningItems = [...planningItemsBase, ...githubPlanning];
      const riskItems = [...riskItemsBase, ...githubRisks];
      
      // core/edge 선택 여부에 따라 집중 영역 결정
      // 선택된 항목(pass/fail/n/a)은 검증 완료로 간주, 미선택('')은 집중 필요
      const focusAreas: string[] = [];
      if (!qaCore || qaCore === 'n/t') focusAreas.push('core');
      if (!qaEdge || qaEdge === 'n/t') focusAreas.push('edge');
      
      // GitHub 커밋 정보 구성 (선택된 모든 커밋 포함)
      const githubCommits = githubSummary?.commits?.map(c => ({
        sha: c.sha,
        message: c.message,
        author: c.author,
        files: githubSummary.merged?.focus_areas
          ?.filter(f => f.related_files?.length)
          ?.flatMap(f => f.related_files) ?? [],
      }));
      
      const response = await suggestTestcases({
        feature_name: featureName || (lang === 'ko' ? '새 기능' : 'New Feature'),
        planning_intent: planningItems,
        risks: riskItems,
        lang: lang as 'ko' | 'en',
        focus_areas: focusAreas.length > 0 ? focusAreas : undefined,
        github_commits: githubCommits && githubCommits.length > 0 ? githubCommits : undefined,
      });
      
      // GitHub 커밋 정보가 있는 경우, 테스트 케이스에 커밋 정보 추가 (source는 유지)
      let testcasesWithGithubInfo = response.testcases;
      if (githubSummary && (githubPlanning.length > 0 || githubRisks.length > 0)) {
        // 커밋 SHA 리스트 (첫 번째 커밋 사용)
        const firstCommit = githubSummary.commits[0];
        testcasesWithGithubInfo = response.testcases.map(tc => ({
          ...tc,
          // 원래 source(planning/risk)는 유지하고, GitHub 메타데이터만 추가
          github_commit_sha: firstCommit?.sha,
          github_commit_message: firstCommit?.message?.split('\n')[0]?.substring(0, 50),
          github_repo: githubSummary.repoFullName,
        }));
      }
      
      setSuggestedTestcases(testcasesWithGithubInfo);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate checklist');
    } finally {
      updateIsGenerating(false);
    }
  }, [user, isDemo, featureName, planningIntent, risks, githubSummary, lang, qaCore, qaEdge, setSuggestedTestcases, onGeneratingChange]);

  // 테스트 케이스 상태 변경 (상태 변경 시 진행자만 자동 기록, 수정자 아님)
  const handleStatusChange = useCallback((id: string, status: TestCaseStatus) => {
    const testerName = user?.name || user?.email || undefined;
    setSuggestedTestcases(
      suggestedTestcases.map((tc) =>
        tc.id === id ? { 
          ...tc, 
          status,
          // 상태가 실제로 변경된 경우에만 진행자 기록
          tester: tc.status !== status ? (testerName || tc.tester) : tc.tester,
          testedAt: tc.status !== status ? Date.now() : tc.testedAt,
        } : tc
      )
    );
  }, [suggestedTestcases, setSuggestedTestcases, user]);

  // 테스트 케이스 메모 변경
  const handleMemoChange = useCallback((id: string, memo: string) => {
    setSuggestedTestcases(
      suggestedTestcases.map((tc) =>
        tc.id === id ? { ...tc, memo } : tc
      )
    );
  }, [suggestedTestcases, setSuggestedTestcases]);

  // 테스트 케이스 편집
  const handleEditTestcase = useCallback((id: string, updates: Partial<SuggestedTestCase>) => {
    setSuggestedTestcases(
      suggestedTestcases.map((tc) =>
        tc.id === id ? { ...tc, ...updates } : tc
      )
    );
  }, [suggestedTestcases, setSuggestedTestcases]);

  // 테스트 케이스 삭제
  const handleDeleteTestcase = useCallback((id: string) => {
    setSuggestedTestcases(suggestedTestcases.filter(tc => tc.id !== id));
  }, [suggestedTestcases, setSuggestedTestcases]);

  // 드래그 시작
  const handleDragStart = useCallback((id: string) => {
    setDraggingId(id);
  }, []);

  // 드래그 종료
  const handleDragEnd = useCallback(() => {
    setDraggingId(null);
    setDragOverCategory(null);
  }, []);

  // 드롭 핸들러 - 카테고리 변경
  const handleDrop = useCallback((targetCategory: 'core' | 'edge') => {
    if (draggingId) {
      setSuggestedTestcases(
        suggestedTestcases.map((tc) =>
          tc.id === draggingId ? { ...tc, category: targetCategory } : tc
        )
      );
    }
    setDraggingId(null);
    setDragOverCategory(null);
  }, [draggingId, suggestedTestcases, setSuggestedTestcases]);

  // 초기화
  const handleClear = useCallback(() => {
    setSuggestedTestcases([]);
  }, [setSuggestedTestcases]);

  // 새 카테고리 생성
  const handleCreateCategory = useCallback(async () => {
    if (!newCategoryName.trim()) return;
    
    try {
      const category = await createTestCaseCategory(newCategoryName.trim());
      setCategories(prev => [...prev, category]);
      setSelectedCategoryId(category.id);
      setNewCategoryName('');
    } catch (err) {
      console.error('Failed to create category:', err);
    }
  }, [newCategoryName]);

  // 테스트 케이스 저장
  const handleSaveTestcases = useCallback(async () => {
    if (suggestedTestcases.length === 0) return;
    
    setIsSaving(true);
    setSaveSuccess(false);
    
    try {
      // 선택된 태그들을 쉼표로 결합 (최대 3개)
      const featureNameValue = selectedTags.length > 0 
        ? selectedTags.slice(0, MAX_TAGS).join(', ') 
        : undefined;
      
      const requests: SaveTestCaseRequest[] = suggestedTestcases.map(tc => ({
        category_id: selectedCategoryId,
        original_id: tc.id,
        test_category: tc.category,
        title: tc.title,
        description: tc.description,
        priority: tc.priority,
        source: tc.source,
        feature_name: featureNameValue,
      }));
      
      await saveTestCasesBulk(requests);
      setSaveSuccess(true);
      setShowSaveModal(false);
      
      // 3초 후 성공 메시지 숨김
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save checklist');
    } finally {
      setIsSaving(false);
    }
  }, [suggestedTestcases, selectedCategoryId, selectedTags, featureName]);

  // 저장된 테스트케이스 불러오기 (중복 방지)
  const handleLoadTestcase = useCallback((saved: SavedTestCaseResponse) => {
    // 동일한 제목과 설명을 가진 테스트케이스가 이미 있는지 확인
    const isDuplicate = suggestedTestcases.some(
      tc => tc.title === saved.title && tc.description === saved.description
    );
    
    if (isDuplicate) {
      // 이미 존재하는 경우 알림
      alert(lang === 'ko' 
        ? '이미 동일한 항목이 존재합니다.' 
        : 'This item already exists.');
      return;
    }
    
    const newTestcase: SuggestedTestCase = {
      id: `loaded_${saved.id}_${Date.now()}`,
      category: saved.test_category as 'core' | 'edge',
      title: saved.title,
      description: saved.description,
      priority: saved.priority as 'high' | 'medium' | 'low',
      status: 'n/t',
      source: saved.source as 'planning' | 'risk' | undefined,
    };
    setSuggestedTestcases([...suggestedTestcases, newTestcase]);
  }, [suggestedTestcases, setSuggestedTestcases, lang]);

  // 저장된 테스트케이스 삭제
  const handleDeleteSavedTestcase = useCallback(async (id: number) => {
    try {
      await deleteSavedTestCase(id);
      setSavedTestcases(prev => prev.filter(tc => tc.id !== id));
    } catch (err) {
      console.error('Failed to delete saved checklist item:', err);
    }
  }, []);

  // 요약 계산
  const summary = useMemo(() => {
    const counts = { pass: 0, fail: 0, 'n/a': 0, 'n/t': 0 };
    suggestedTestcases.forEach((tc) => {
      counts[tc.status]++;
    });
    return counts;
  }, [suggestedTestcases]);

  // 카테고리별 분류
  const { coreTestcases, edgeTestcases } = useMemo(() => {
    const core = suggestedTestcases.filter((tc) => tc.category === 'core');
    const edge = suggestedTestcases.filter((tc) => tc.category === 'edge');
    return { coreTestcases: core, edgeTestcases: edge };
  }, [suggestedTestcases]);

  const tc = t.suggestedTestcases;
  
  // 생성 가능 여부 체크 (기획 의도 또는 리스크가 있어야 함 + 플랜 권한)
  const hasContent = !!(planningIntent.trim() || risks.trim());

  return (
    <div className="card p-4 relative">
      {/* 로딩 오버레이 */}
      {isGenerating && (
        <div className="absolute inset-0 bg-surface-base/80 backdrop-blur-sm rounded-lg flex items-center justify-center z-10">
          <div className="flex flex-col items-center gap-3">
            <div className="relative">
              <div className="w-10 h-10 border-2 border-purple-500/30 rounded-full"></div>
              <div className="absolute inset-0 w-10 h-10 border-2 border-transparent border-t-purple-500 rounded-full animate-spin"></div>
            </div>
            <div className="text-center">
              <p className="text-purple-400 text-sm font-medium">
                {tc?.generating || 'Generating checklist...'}
              </p>
              <p className="text-terminal-dim text-xs mt-1">
                {lang === 'ko' ? 'AI가 체크리스트를 생성 중입니다' : 'AI is generating checklist'}
              </p>
            </div>
          </div>
        </div>
      )}
      
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-purple-400">🧪</span>
            <h3 className="text-terminal-text font-medium text-sm">
              {tc?.title || 'AI Suggested Checklist'}
            </h3>
            <span className="px-1.5 py-0.5 bg-purple-500/20 text-purple-400 text-xs font-semibold rounded">
              AI
            </span>
          </div>
          <p className="text-terminal-dim text-xs mt-1">
            {tc?.subtitle || 'AI suggests checklist based on planning intent, risks and commits'}
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          {/* 저장된 테스트케이스 불러오기 버튼 */}
          {!isDemo && user && (
            <button
              type="button"
              onClick={() => setShowLoadModal(true)}
              className="px-3 py-1.5 text-xs border border-purple-500/50 text-purple-400 rounded hover:bg-purple-500/10 transition-colors flex items-center gap-1"
            >
              <span>📂</span>
              <span>{lang === 'ko' ? '불러오기' : 'Load'}</span>
            </button>
          )}
          {suggestedTestcases.length > 0 && (
            <>
              <button
                type="button"
                onClick={handleClear}
                className="px-3 py-1.5 text-xs border border-terminal-border text-terminal-muted rounded hover:border-status-danger hover:text-status-danger transition-colors"
              >
                {tc?.clear || 'Clear'}
              </button>
              <button
                type="button"
                onClick={() => setShowSaveModal(true)}
                className="px-3 py-1.5 text-xs border border-status-go/50 text-status-go rounded hover:bg-status-go/10 transition-colors flex items-center gap-1"
              >
                <span>💾</span>
                <span>{lang === 'ko' ? '저장' : 'Save'}</span>
              </button>
            </>
          )}
          {/* AI 생성 버튼 - 플랜별 분기 */}
          {canUseAIGenerate ? (
            // Max/Team 플랜: 정상 사용
            <button
              type="button"
              onClick={handleGenerate}
              disabled={isGenerating || !hasContent || isDemo || !user}
              className={`
                px-4 py-1.5 text-xs rounded font-medium transition-all
                ${isGenerating || !hasContent || isDemo || !user
                  ? 'bg-terminal-input border border-terminal-border text-terminal-dim cursor-not-allowed'
                  : 'bg-purple-500/20 border border-purple-500/50 text-purple-400 hover:bg-purple-500/30'
                }
              `}
            >
              {isGenerating
                ? (tc?.generating || 'Generating...')
                : suggestedTestcases.length > 0
                  ? (tc?.regenerate || 'Regenerate')
                  : (tc?.generateButton || 'Generate Checklist')
              }
            </button>
          ) : (
            // Pro/Free 플랜: 잠금 표시 (Max/Team 플랜 전용)
            <div className="relative group">
              <button
                type="button"
                disabled
                className="px-4 py-1.5 text-xs rounded font-medium bg-terminal-input border border-terminal-border text-terminal-dim cursor-not-allowed flex items-center gap-1.5"
              >
                <span>🔒</span>
                <span>{tc?.generateButton || 'Generate Checklist'}</span>
                <span className="px-1.5 py-0.5 bg-purple-500/20 text-purple-400 text-[10px] rounded ml-1 font-semibold">
                  MAX+
                </span>
              </button>
              {/* 툴팁 */}
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-[#1a1a2e] border border-purple-500/30 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                <p className="text-xs text-purple-400 font-medium mb-1 flex items-center gap-1.5">
                  <span>🔒</span>
                  <span>{tc?.aiGenerateLocked || 'AI 테스트케이스 생성'}</span>
                </p>
                <p className="text-xs text-terminal-muted">
                  {lang === 'ko' ? 'Max/Team 플랜 전용 기능입니다' : 'Max/Team plan exclusive feature'}
                </p>
                <p className="text-xs text-purple-400/70 mt-1">
                  {lang === 'ko' ? '업그레이드하여 AI 체크리스트 생성을 이용하세요' : 'Upgrade to use AI checklist generation'}
                </p>
                <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-purple-500/30"></div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* 저장 성공 메시지 */}
      {saveSuccess && (
        <div className="p-3 bg-status-go/10 border border-status-go/30 rounded text-sm text-status-go mb-3 flex items-center gap-2">
          <span>✓</span>
          <span>{lang === 'ko' ? '체크리스트가 저장되었습니다!' : 'Checklist saved successfully!'}</span>
        </div>
      )}
      
      {/* 저장 모달 */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setShowSaveModal(false)}>
          <div className="bg-[#1a1a2e] border border-terminal-border rounded-lg p-4 w-full max-w-md mx-4 shadow-2xl" onClick={e => e.stopPropagation()}>
            <h4 className="text-terminal-text font-medium mb-4 flex items-center gap-2">
              <span>💾</span>
              <span>{lang === 'ko' ? '체크리스트 저장' : 'Save Checklist'}</span>
            </h4>
            
            {/* 카테고리 선택 */}
            <div className="mb-4">
              <label className="text-terminal-dim text-xs mb-2 block">
                {lang === 'ko' ? '카테고리 선택' : 'Select Category'}
              </label>
              <select
                value={selectedCategoryId || ''}
                onChange={(e) => setSelectedCategoryId(e.target.value ? Number(e.target.value) : undefined)}
                className="w-full bg-terminal-bg border border-terminal-border rounded px-3 py-2 text-sm text-terminal-text focus:outline-none focus:border-purple-500/50"
              >
                <option value="">{lang === 'ko' ? '카테고리 없음' : 'No Category'}</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name} ({cat.testcase_count})</option>
                ))}
              </select>
            </div>
            
            {/* 새 카테고리 생성 */}
            <div className="mb-4">
              <label className="text-terminal-dim text-xs mb-2 block">
                {lang === 'ko' ? '또는 새 카테고리 생성' : 'Or create new category'}
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder={lang === 'ko' ? '카테고리 이름' : 'Category name'}
                  className="flex-1 bg-terminal-bg border border-terminal-border rounded px-3 py-2 text-sm text-terminal-text placeholder:text-terminal-dim focus:outline-none focus:border-purple-500/50"
                />
                <button
                  type="button"
                  onClick={handleCreateCategory}
                  disabled={!newCategoryName.trim()}
                  className="px-3 py-2 text-xs bg-purple-500/20 border border-purple-500/50 text-purple-400 rounded hover:bg-purple-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {lang === 'ko' ? '추가' : 'Add'}
                </button>
              </div>
            </div>
            
            {/* 기능 태그 입력 (최대 3개, 쉼표 구분) */}
            <div className="mb-4 relative">
              <label className="text-terminal-dim text-xs mb-2 flex items-center justify-between">
                <span>🏷️ {lang === 'ko' ? '기능 태그' : 'Feature Tags'}</span>
                <span className="text-terminal-muted">
                  {selectedTags.length}/{MAX_TAGS}
                </span>
              </label>
              
              {/* 선택된 태그 블록 + 입력 필드 */}
              <div className="flex flex-wrap items-center gap-1.5 p-2 bg-terminal-bg border border-terminal-border rounded min-h-[42px]">
                {/* 선택된 태그들 */}
                {selectedTags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-purple-500/20 border border-purple-500/50 rounded text-xs text-purple-400"
                  >
                    <span>🏷️</span>
                    <span>{tag}</span>
                    <button
                      type="button"
                      onClick={() => setSelectedTags(prev => prev.filter((_, i) => i !== index))}
                      className="ml-1 hover:text-red-400 transition-colors"
                    >
                      ×
                    </button>
                  </span>
                ))}
                
                {/* 입력 필드 (최대 개수 미만일 때만 표시) */}
                {selectedTags.length < MAX_TAGS && (
                  <div className="relative flex-1 min-w-[120px]">
                    <input
                      type="text"
                      value={tagInput}
                      onChange={(e) => {
                        const value = e.target.value;
                        // 쉼표 입력 시 태그 추가
                        if (value.includes(',')) {
                          const newTag = value.replace(',', '').trim();
                          if (newTag && !selectedTags.includes(newTag) && selectedTags.length < MAX_TAGS) {
                            setSelectedTags(prev => [...prev, newTag]);
                          }
                          setTagInput('');
                          setShowTagSuggestions(false);
                        } else {
                          setTagInput(value);
                          setShowTagSuggestions(true);
                        }
                      }}
                      onKeyDown={(e) => {
                        // Enter로 태그 추가
                        if (e.key === 'Enter' && tagInput.trim()) {
                          e.preventDefault();
                          const newTag = tagInput.trim();
                          if (!selectedTags.includes(newTag) && selectedTags.length < MAX_TAGS) {
                            setSelectedTags(prev => [...prev, newTag]);
                          }
                          setTagInput('');
                          setShowTagSuggestions(false);
                        }
                        // Backspace로 마지막 태그 삭제
                        if (e.key === 'Backspace' && !tagInput && selectedTags.length > 0) {
                          setSelectedTags(prev => prev.slice(0, -1));
                        }
                      }}
                      onFocus={() => setShowTagSuggestions(true)}
                      onBlur={() => setTimeout(() => setShowTagSuggestions(false), 200)}
                      placeholder={
                        selectedTags.length === 0
                          ? (lang === 'ko' ? '태그 입력 (쉼표로 구분)' : 'Enter tags (comma separated)')
                          : (lang === 'ko' ? '추가...' : 'Add...')
                      }
                      className="w-full bg-transparent text-sm text-terminal-text placeholder:text-terminal-dim focus:outline-none"
                    />
                    
                    {/* 자동완성 드롭다운 */}
                    {showTagSuggestions && existingTags.length > 0 && (
                      <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-[#1a1a2e] border border-terminal-border rounded shadow-lg max-h-40 overflow-y-auto">
                        {existingTags
                          .filter(tag => 
                            !selectedTags.includes(tag) && 
                            (tag.toLowerCase().includes(tagInput.toLowerCase()) || !tagInput)
                          )
                          .slice(0, 8)
                          .map(tag => (
                            <button
                              key={tag}
                              type="button"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                if (selectedTags.length < MAX_TAGS) {
                                  setSelectedTags(prev => [...prev, tag]);
                                }
                                setTagInput('');
                                setShowTagSuggestions(false);
                              }}
                              className="w-full px-3 py-2 text-left text-sm text-terminal-text hover:bg-purple-500/20 flex items-center gap-2 transition-colors"
                            >
                              <span className="text-purple-400">🏷️</span>
                              <span>{tag}</span>
                            </button>
                          ))
                        }
                        {tagInput && !existingTags.includes(tagInput) && !selectedTags.includes(tagInput) && (
                          <div className="px-3 py-2 text-xs text-cyan-400 border-t border-terminal-border">
                            {lang === 'ko' ? `"${tagInput}" 새 태그로 추가 (Enter 또는 쉼표)` : `"${tagInput}" will be added as new tag (Enter or comma)`}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <p className="text-terminal-dim text-xs mt-1.5">
                💡 {lang === 'ko' ? '쉼표(,) 또는 Enter로 태그 추가 • 최대 3개' : 'Add tags with comma (,) or Enter • Max 3 tags'}
              </p>
              
              {/* 추천 태그 (현재 기능명 기반) */}
              {existingTags.length > 0 && (
                <div className="mt-3">
                  <p className="text-terminal-dim text-xs mb-2">
                    ✨ {lang === 'ko' ? '기존 태그' : 'Existing Tags'}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {existingTags
                      .filter(tag => !selectedTags.includes(tag))
                      .slice(0, 8)
                      .map(tag => (
                        <button
                          key={tag}
                          type="button"
                          onClick={() => {
                            if (selectedTags.length < MAX_TAGS) {
                              setSelectedTags(prev => [...prev, tag]);
                            }
                          }}
                          disabled={selectedTags.length >= MAX_TAGS}
                          className={`px-2 py-1 text-xs rounded transition-colors flex items-center gap-1 ${
                            selectedTags.length >= MAX_TAGS
                              ? 'bg-terminal-input border border-terminal-border text-terminal-dim cursor-not-allowed opacity-50'
                              : 'bg-terminal-input border border-terminal-border text-terminal-muted hover:text-terminal-text hover:border-cyan-500/50'
                          }`}
                        >
                          <span>🏷️</span>
                          <span>{tag}</span>
                        </button>
                      ))
                    }
                    {existingTags.filter(tag => !selectedTags.includes(tag)).length === 0 && (
                      <span className="text-terminal-dim text-xs italic">
                        {lang === 'ko' ? '모든 태그가 선택되었습니다' : 'All tags selected'}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            {/* 저장할 테스트케이스 수 */}
            <div className="mb-4 p-3 bg-terminal-bg rounded border border-terminal-border">
              <p className="text-terminal-muted text-xs">
                {lang === 'ko' 
                  ? `${suggestedTestcases.length}개의 체크리스트를 저장합니다`
                  : `Saving ${suggestedTestcases.length} checklist items`}
              </p>
              {selectedTags.length > 0 && (
                <div className="flex flex-wrap items-center gap-1 mt-1">
                  <span className="text-purple-400 text-xs">🏷️</span>
                  {selectedTags.map((tag, idx) => (
                    <span key={idx} className="px-1.5 py-0.5 bg-purple-500/20 border border-purple-500/30 rounded text-xs text-purple-400">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
            
            {/* 버튼 */}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowSaveModal(false)}
                className="px-4 py-2 text-xs border border-terminal-border text-terminal-muted rounded hover:text-terminal-text transition-colors"
              >
                {lang === 'ko' ? '취소' : 'Cancel'}
              </button>
              <button
                type="button"
                onClick={handleSaveTestcases}
                disabled={isSaving}
                className="px-4 py-2 text-xs bg-status-go/20 border border-status-go/50 text-status-go rounded hover:bg-status-go/30 disabled:opacity-50 transition-colors flex items-center gap-1"
              >
                {isSaving ? (
                  <>
                    <span className="animate-spin">⏳</span>
                    <span>{lang === 'ko' ? '저장 중...' : 'Saving...'}</span>
                  </>
                ) : (
                  <>
                    <span>💾</span>
                    <span>{lang === 'ko' ? '저장' : 'Save'}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 불러오기 모달 */}
      {showLoadModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setShowLoadModal(false)}>
          <div className="bg-[#1a1a2e] border border-terminal-border rounded-lg p-4 w-full max-w-2xl mx-4 shadow-2xl max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <h4 className="text-terminal-text font-medium mb-4 flex items-center gap-2">
              <span>📂</span>
              <span>{lang === 'ko' ? '저장된 체크리스트' : 'Saved Checklist'}</span>
            </h4>
            
            {/* 카테고리/기능 태그 필터 - 저장된 테스트케이스가 있을 때만 표시 */}
            {!isLoadingSaved && savedTestcases.length > 0 && (
              <>
                {/* 카테고리 필터 */}
                <div className="mb-3">
                  <label className="text-terminal-dim text-xs mb-1.5 block">📁 {lang === 'ko' ? '카테고리' : 'Category'}</label>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => setLoadingCategory(undefined)}
                      className={`px-3 py-1.5 text-xs rounded transition-colors ${
                        loadingCategory === undefined
                          ? 'bg-purple-500/30 border border-purple-500 text-purple-400'
                          : 'bg-terminal-input border border-terminal-border text-terminal-muted hover:text-terminal-text'
                      }`}
                    >
                      {lang === 'ko' ? '전체' : 'All'}
                    </button>
                    {categories.map(cat => (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => setLoadingCategory(cat.id)}
                        className={`px-3 py-1.5 text-xs rounded transition-colors ${
                          loadingCategory === cat.id
                            ? 'bg-purple-500/30 border border-purple-500 text-purple-400'
                            : 'bg-terminal-input border border-terminal-border text-terminal-muted hover:text-terminal-text'
                        }`}
                      >
                        {cat.name} ({cat.testcase_count})
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* 기능 태그 필터 */}
                <div className="mb-4">
                  <label className="text-terminal-dim text-xs mb-1.5 block">🏷️ {lang === 'ko' ? '기능 태그' : 'Feature Tag'}</label>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => setLoadFeatureFilter(undefined)}
                      className={`px-3 py-1.5 text-xs rounded transition-colors ${
                        loadFeatureFilter === undefined
                          ? 'bg-cyan-500/30 border border-cyan-500 text-cyan-400'
                          : 'bg-terminal-input border border-terminal-border text-terminal-muted hover:text-terminal-text'
                      }`}
                    >
                      {lang === 'ko' ? '전체' : 'All'}
                    </button>
                    {/* 태그 없음 옵션 */}
                    {(() => {
                      const noTagCount = savedTestcases.filter(tc => !tc.feature_name).length;
                      return noTagCount > 0 && (
                        <button
                          type="button"
                          onClick={() => setLoadFeatureFilter('__no_tag__')}
                          className={`px-3 py-1.5 text-xs rounded transition-colors flex items-center gap-1 ${
                            loadFeatureFilter === '__no_tag__'
                              ? 'bg-gray-500/30 border border-gray-500 text-gray-400'
                              : 'bg-terminal-input border border-terminal-border text-terminal-muted hover:text-terminal-text'
                          }`}
                        >
                          <span>🚫</span>
                          <span>{lang === 'ko' ? '태그 없음' : 'No Tag'}</span>
                          <span className="text-[10px] opacity-70">({noTagCount})</span>
                        </button>
                      );
                    })()}
                    {existingTags.map(tag => {
                      const count = savedTestcases.filter(tc => tc.feature_name === tag).length;
                      return (
                        <button
                          key={tag}
                          type="button"
                          onClick={() => setLoadFeatureFilter(tag)}
                          className={`px-3 py-1.5 text-xs rounded transition-colors flex items-center gap-1 ${
                            loadFeatureFilter === tag
                              ? 'bg-cyan-500/30 border border-cyan-500 text-cyan-400'
                              : 'bg-terminal-input border border-terminal-border text-terminal-muted hover:text-terminal-text'
                          }`}
                        >
                          <span>🏷️</span>
                          <span>{tag}</span>
                          <span className="text-[10px] opacity-70">({count})</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
            
            {/* 테스트케이스 목록 */}
            <div className="flex-1 overflow-y-auto space-y-2 min-h-[200px]">
              {isLoadingSaved ? (
                <div className="flex items-center justify-center h-32 text-terminal-dim">
                  <span className="animate-spin mr-2">⏳</span>
                  {lang === 'ko' ? '불러오는 중...' : 'Loading...'}
                </div>
              ) : savedTestcases.length === 0 ? (
                <div className="flex items-center justify-center h-32 text-terminal-dim text-sm">
                  {lang === 'ko' ? '저장된 체크리스트가 없습니다' : 'No saved checklist items'}
                </div>
              ) : (
                savedTestcases
                  .filter(tc => {
                    if (!loadFeatureFilter) return true;
                    if (loadFeatureFilter === '__no_tag__') return !tc.feature_name;
                    // 쉼표로 구분된 태그들 중 하나라도 일치하면 포함
                    if (!tc.feature_name) return false;
                    const tcTags = tc.feature_name.split(',').map(t => t.trim());
                    return tcTags.includes(loadFeatureFilter);
                  })
                  .map(saved => (
                  <div 
                    key={saved.id} 
                    className="p-3 bg-terminal-input rounded-lg border border-terminal-border flex items-start justify-between gap-3 cursor-pointer hover:bg-terminal-dim/30 hover:border-purple-500/50 transition-all group"
                    onClick={() => {
                      handleLoadTestcase(saved);
                      setShowLoadModal(false);
                    }}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-xs mb-1 flex-wrap">
                        <span className={`px-1.5 py-0.5 rounded ${saved.test_category === 'core' ? 'bg-status-go/20 text-status-go' : 'bg-status-warning/20 text-status-warning'}`}>
                          {saved.test_category}
                        </span>
                        <span className={`${saved.priority === 'high' ? 'text-status-danger' : saved.priority === 'medium' ? 'text-status-warning' : 'text-terminal-muted'}`}>
                          {saved.priority}
                        </span>
                        {saved.category_name && (
                          <span className="text-purple-400">📁 {saved.category_name}</span>
                        )}
                        {saved.feature_name && (
                          <span className="flex items-center gap-1 flex-wrap">
                            {saved.feature_name.split(',').map((tag, idx) => (
                              <span key={idx} className="px-1 py-0.5 bg-cyan-500/20 border border-cyan-500/30 rounded text-cyan-400 text-xs">
                                🏷️ {tag.trim()}
                              </span>
                            ))}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-terminal-text font-medium truncate group-hover:text-purple-300 transition-colors">{saved.title}</p>
                      <p className="text-xs text-terminal-muted mt-1 line-clamp-2">{saved.description}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-xs text-purple-400 opacity-0 group-hover:opacity-100 transition-opacity">
                        {lang === 'ko' ? '클릭하여 추가' : 'Click to add'}
                      </span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteSavedTestcase(saved.id);
                        }}
                        className="px-2 py-1.5 text-xs border border-terminal-border text-terminal-muted rounded hover:border-status-danger hover:text-status-danger transition-colors"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
            
            {/* 닫기 버튼 */}
            <div className="flex justify-end mt-4 pt-4 border-t border-terminal-border">
              <button
                type="button"
                onClick={() => setShowLoadModal(false)}
                className="px-4 py-2 text-xs border border-terminal-border text-terminal-muted rounded hover:text-terminal-text transition-colors"
              >
                {lang === 'ko' ? '닫기' : 'Close'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 에러 표시 */}
      {error && (
        <div className="p-3 bg-status-danger/10 border border-status-danger/30 rounded text-sm text-status-danger mb-3">
          {error}
        </div>
      )}

      {/* 테스트 케이스가 없을 때 */}
      {suggestedTestcases.length === 0 && !isGenerating && (
        <div className="p-6 border border-dashed border-terminal-border rounded-lg text-center">
          <div className="text-terminal-dim text-sm">
            {tc?.noTestcases || 'No checklist items generated yet'}
          </div>
          <div className="text-terminal-muted text-xs mt-1">
            {tc?.generateHint || 'Enter planning intent, risks, or commits, then generate checklist'}
          </div>
        </div>
      )}

      {/* 테스트 케이스 목록 */}
      {suggestedTestcases.length > 0 && (
        <div className="space-y-4">
          {/* 요약 */}
          <div className="flex items-center gap-4 p-3 bg-terminal-input rounded-lg text-xs">
            <span className="text-terminal-muted">{tc?.summary || 'Summary'}:</span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-status-go"></span>
              <span className="text-status-go">{summary.pass} {tc?.passCount || 'Pass'}</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-status-danger"></span>
              <span className="text-status-danger">{summary.fail} {tc?.failCount || 'Fail'}</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-terminal-muted"></span>
              <span className="text-terminal-muted">{summary['n/a']} {tc?.naCount || 'N/A'}</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-terminal-dim"></span>
              <span className="text-terminal-dim">{summary['n/t']} {tc?.ntCount || 'N/T'}</span>
            </span>
          </div>

          {/* 핵심 테스트 케이스 (드롭 영역) */}
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragOverCategory('core');
            }}
            onDragLeave={() => setDragOverCategory(null)}
            onDrop={(e) => {
              e.preventDefault();
              handleDrop('core');
            }}
            onDragEnd={handleDragEnd}
            className={`p-3 rounded-lg transition-colors ${
              dragOverCategory === 'core' 
                ? 'bg-status-go/10 border-2 border-dashed border-status-go/50' 
                : 'bg-transparent'
            } ${coreTestcases.length === 0 ? 'min-h-[60px] border border-dashed border-terminal-border' : ''}`}
          >
            <div className="flex items-center gap-2 mb-2">
              <span className="text-status-go text-xs font-semibold uppercase">
                {tc?.coreCategory || 'Core'}
              </span>
              <span className="text-terminal-dim text-xs">({coreTestcases.length})</span>
              {draggingId && (
                <span className="text-xs text-terminal-dim ml-auto">
                  ↓ 여기로 드래그
                </span>
              )}
            </div>
            {coreTestcases.length > 0 ? (
              <div className="space-y-2">
                {coreTestcases.map((testcase) => (
                  <TestCaseCard
                    key={testcase.id}
                    testcase={testcase}
                    onStatusChange={handleStatusChange}
                    onMemoChange={handleMemoChange}
                    onEdit={handleEditTestcase}
                    onDelete={handleDeleteTestcase}
                    onDragStart={handleDragStart}
                    tc={tc || {}}
                  />
                ))}
              </div>
            ) : draggingId ? (
              <p className="text-terminal-dim text-xs text-center py-2">
                이곳에 드롭하면 Core로 이동합니다
              </p>
            ) : null}
          </div>

          {/* 엣지 테스트 케이스 (드롭 영역) */}
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragOverCategory('edge');
            }}
            onDragLeave={() => setDragOverCategory(null)}
            onDrop={(e) => {
              e.preventDefault();
              handleDrop('edge');
            }}
            onDragEnd={handleDragEnd}
            className={`p-3 rounded-lg transition-colors ${
              dragOverCategory === 'edge' 
                ? 'bg-status-warning/10 border-2 border-dashed border-status-warning/50' 
                : 'bg-transparent'
            } ${edgeTestcases.length === 0 ? 'min-h-[60px] border border-dashed border-terminal-border' : ''}`}
          >
            <div className="flex items-center gap-2 mb-2">
              <span className="text-status-warning text-xs font-semibold uppercase">
                {tc?.edgeCategory || 'Edge'}
              </span>
              <span className="text-terminal-dim text-xs">({edgeTestcases.length})</span>
              {draggingId && (
                <span className="text-xs text-terminal-dim ml-auto">
                  ↓ 여기로 드래그
                </span>
              )}
            </div>
            {edgeTestcases.length > 0 ? (
              <div className="space-y-2">
                {edgeTestcases.map((testcase) => (
                  <TestCaseCard
                    key={testcase.id}
                    testcase={testcase}
                    onStatusChange={handleStatusChange}
                    onMemoChange={handleMemoChange}
                    onEdit={handleEditTestcase}
                    onDelete={handleDeleteTestcase}
                    onDragStart={handleDragStart}
                    tc={tc || {}}
                  />
                ))}
              </div>
            ) : draggingId ? (
              <p className="text-terminal-dim text-xs text-center py-2">
                이곳에 드롭하면 Edge로 이동합니다
              </p>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

// 테스트 케이스 카드 컴포넌트
interface TestCaseCardProps {
  testcase: SuggestedTestCase;
  onStatusChange: (id: string, status: TestCaseStatus) => void;
  onMemoChange: (id: string, memo: string) => void;
  onEdit: (id: string, updates: Partial<SuggestedTestCase>) => void;
  onDelete: (id: string) => void;
  onDragStart: (id: string) => void;
  tc: Record<string, string>;
}

function TestCaseCard({ testcase, onStatusChange, onMemoChange, onEdit, onDelete, onDragStart, tc }: TestCaseCardProps) {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownButtonRef = useRef<HTMLButtonElement>(null);
  const [showMemo, setShowMemo] = useState(!!testcase.memo);
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(testcase.title);
  const [editDescription, setEditDescription] = useState(testcase.description);
  const [editPriority, setEditPriority] = useState(testcase.priority);
  const [editCategory, setEditCategory] = useState(testcase.category);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const { lang } = useLanguage();

  const currentStatus = statusConfig[testcase.status];
  const priorityLabel = {
    high: tc?.priorityHigh || 'High',
    medium: tc?.priorityMedium || 'Medium',
    low: tc?.priorityLow || 'Low',
  }[testcase.priority];

  const priorityColor = {
    high: 'text-status-danger',
    medium: 'text-status-warning',
    low: 'text-terminal-muted',
  }[testcase.priority];

  const sourceLabel = testcase.source === 'risk'
    ? (tc?.sourceRisk || 'Risk')
    : testcase.source === 'commit'
    ? (tc?.sourceCommit || 'Commit')
    : (tc?.sourcePlanning || 'Planning');

  const handleSaveEdit = () => {
    // 내용이 실제로 변경되었는지 확인
    const hasContentChanged = 
      editTitle.trim() !== testcase.title ||
      editDescription.trim() !== testcase.description ||
      editPriority !== testcase.priority ||
      editCategory !== testcase.category;
    
    onEdit(testcase.id, {
      title: editTitle.trim() || testcase.title,
      description: editDescription.trim() || testcase.description,
      priority: editPriority,
      category: editCategory,
      // 내용이 변경된 경우에만 수정자 기록 + 이전 버전 저장
      ...(hasContentChanged && {
        modifier: user?.name || user?.email || undefined,
        modifiedAt: Date.now(),
        previousVersion: {
          title: testcase.title,
          description: testcase.description,
          priority: testcase.priority,
          category: testcase.category,
          modifiedAt: testcase.modifiedAt || Date.now(),
        },
      }),
    });
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setEditTitle(testcase.title);
    setEditDescription(testcase.description);
    setEditPriority(testcase.priority);
    setEditCategory(testcase.category);
    setIsEditing(false);
  };

  // 편집 모드
  if (isEditing) {
    return (
      <div className="border border-purple-500/50 rounded-lg p-3 bg-purple-500/5">
        <div className="space-y-3">
          {/* 제목 편집 */}
          <div>
            <label className="text-xs text-terminal-dim block mb-1">제목</label>
            <input
              type="text"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              className="w-full bg-terminal-bg border border-terminal-border rounded px-2 py-1.5 text-sm text-terminal-text focus:outline-none focus:border-purple-500/50"
            />
          </div>
          
          {/* 설명 편집 */}
          <div>
            <label className="text-xs text-terminal-dim block mb-1">설명</label>
            <textarea
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              className="w-full bg-terminal-bg border border-terminal-border rounded px-2 py-1.5 text-xs text-terminal-text focus:outline-none focus:border-purple-500/50 resize-none"
              rows={2}
            />
          </div>
          
          {/* 우선순위 & 카테고리 */}
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="text-xs text-terminal-dim block mb-1">우선순위</label>
              <select
                value={editPriority}
                onChange={(e) => setEditPriority(e.target.value as 'high' | 'medium' | 'low')}
                className="w-full bg-terminal-bg border border-terminal-border rounded px-2 py-1.5 text-xs text-terminal-text focus:outline-none focus:border-purple-500/50"
              >
                <option value="high">🔥 High</option>
                <option value="medium">⚡ Medium</option>
                <option value="low">💤 Low</option>
              </select>
            </div>
            <div className="flex-1">
              <label className="text-xs text-terminal-dim block mb-1">카테고리</label>
              <select
                value={editCategory}
                onChange={(e) => setEditCategory(e.target.value as 'core' | 'edge')}
                className="w-full bg-terminal-bg border border-terminal-border rounded px-2 py-1.5 text-xs text-terminal-text focus:outline-none focus:border-purple-500/50"
              >
                <option value="core">Core</option>
                <option value="edge">Edge</option>
              </select>
            </div>
          </div>
          
          {/* 버튼 */}
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-terminal-border">
            <button
              type="button"
              onClick={handleCancelEdit}
              className="px-3 py-1.5 text-xs text-terminal-muted hover:text-terminal-text transition-colors"
            >
              취소
            </button>
            <button
              type="button"
              onClick={handleSaveEdit}
              className="px-3 py-1.5 text-xs bg-purple-500/20 border border-purple-500/50 text-purple-400 rounded hover:bg-purple-500/30 transition-colors"
            >
              저장
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`border border-terminal-border rounded-lg p-3 ${currentStatus.bg} group cursor-grab active:cursor-grabbing`}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('testcaseId', testcase.id);
        onDragStart(testcase.id);
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-terminal-text text-sm font-medium truncate">
              {testcase.title}
            </span>
            <span className={`text-xs ${priorityColor}`}>
              {priorityLabel}
            </span>
            {testcase.source && (
              <span className="text-xs text-terminal-dim px-1.5 py-0.5 bg-terminal-bg rounded">
                {sourceLabel}
              </span>
            )}
            
            {/* 편집/삭제 버튼 - 호버 시 표시 (레이아웃 고정) */}
            <div className="flex items-center gap-1 ml-auto opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                type="button"
                onClick={() => setIsEditing(true)}
                className="p-1 text-terminal-dim hover:text-purple-400 transition-colors"
                title="편집"
              >
                ✏️
              </button>
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                className="p-1 text-terminal-dim hover:text-status-danger transition-colors"
                title="삭제"
              >
                🗑️
              </button>
            </div>
          </div>
          
          {/* 삭제 확인 대화상자 */}
          {showDeleteConfirm && (
            <div className="mt-2 p-2 bg-status-danger/10 border border-status-danger/30 rounded">
              <p className="text-xs text-status-danger mb-2">
                ⚠️ 이 체크리스트 항목을 삭제하시겠습니까? 삭제 후 복구할 수 없습니다.
              </p>
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-2 py-1 text-xs text-terminal-muted hover:text-terminal-text transition-colors"
                >
                  취소
                </button>
                <button
                  type="button"
                  onClick={() => {
                    onDelete(testcase.id);
                    setShowDeleteConfirm(false);
                  }}
                  className="px-2 py-1 text-xs bg-status-danger/20 border border-status-danger/50 text-status-danger rounded hover:bg-status-danger/30 transition-colors"
                >
                  삭제
                </button>
              </div>
            </div>
          )}
          <p className="text-terminal-muted text-xs mt-1 line-clamp-2">
            {testcase.description}
          </p>
          
          {/* 수정자 정보 표시 */}
          {testcase.modifier && (
            <div className="mt-2 flex flex-wrap items-center gap-2 text-[10px]">
              {testcase.modifier && testcase.previousVersion && (
                <button
                  type="button"
                  onClick={() => setShowHistoryModal(true)}
                  className="px-1.5 py-0.5 bg-orange-500/10 border border-orange-500/30 rounded text-orange-400 flex items-center gap-1 hover:bg-orange-500/20 transition-colors cursor-pointer"
                >
                  <span>✏️</span>
                  <span>{tc?.modifier || '수정자'}: {testcase.modifier}</span>
                  <span className="text-[8px] opacity-70">👁</span>
                </button>
              )}
              {testcase.modifier && !testcase.previousVersion && (
                <span className="px-1.5 py-0.5 bg-orange-500/10 border border-orange-500/30 rounded text-orange-400 flex items-center gap-1">
                  <span>✏️</span>
                  <span>{tc?.modifier || '수정자'}: {testcase.modifier}</span>
                </span>
              )}
            </div>
          )}
          
          {/* 메모 토글 버튼 */}
          <div className="mt-2 flex items-center gap-3">
            <button
              type="button"
              onClick={() => setShowMemo(!showMemo)}
              className="text-xs text-terminal-dim hover:text-terminal-muted transition-colors flex items-center gap-1"
            >
              <span>📝</span>
              <span>{showMemo ? (tc?.hideMemo || 'Hide memo') : (tc?.addMemo || 'Add memo')}</span>
              {testcase.memo && !showMemo && (
                <span className="text-purple-400 ml-1">•</span>
              )}
            </button>
          </div>
          
          {/* 메모 입력 영역 */}
          {showMemo && (
            <div className="mt-2">
              <textarea
                value={testcase.memo || ''}
                onChange={(e) => onMemoChange(testcase.id, e.target.value)}
                placeholder={tc?.memoPlaceholder || 'Add notes about this checklist item...'}
                className="w-full bg-terminal-bg border border-terminal-border rounded px-2 py-1.5 text-xs text-terminal-text placeholder:text-terminal-dim focus:outline-none focus:border-purple-500/50 resize-none"
                rows={2}
              />
            </div>
          )}
        </div>

        {/* 상태 드롭다운 */}
        <div className="relative flex-shrink-0">
          <button
            ref={dropdownButtonRef}
            type="button"
            onClick={() => {
              if (!isOpen && dropdownButtonRef.current) {
                const rect = dropdownButtonRef.current.getBoundingClientRect();
                const dropdownHeight = 160; // 드롭다운 메뉴 예상 높이
                const spaceBelow = window.innerHeight - rect.bottom;
                setOpenUpward(spaceBelow < dropdownHeight);
              }
              setIsOpen(!isOpen);
            }}
            className={`
              px-3 py-1.5 text-xs font-medium rounded border transition-all
              ${currentStatus.bg} ${currentStatus.text} border-current/30
              hover:brightness-110
            `}
          >
            {currentStatus.label}
            <span className="ml-1">{openUpward && isOpen ? '▲' : '▼'}</span>
          </button>

          {isOpen && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setIsOpen(false)}
              />
              <div className={`absolute right-0 z-20 bg-terminal-bg border border-terminal-border rounded-lg shadow-lg overflow-hidden min-w-[100px] ${openUpward ? 'bottom-full mb-1' : 'top-full mt-1'}`}>
                {(Object.keys(statusConfig) as TestCaseStatus[]).map((status) => (
                  <button
                    key={status}
                    type="button"
                    onClick={() => {
                      onStatusChange(testcase.id, status);
                      setIsOpen(false);
                    }}
                    className={`
                      w-full px-3 py-2 text-xs text-left hover:bg-terminal-input transition-colors
                      ${testcase.status === status ? 'bg-terminal-input' : ''}
                      ${statusConfig[status].text}
                    `}
                  >
                    {statusConfig[status].label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
      
      {/* 수정 이력 모달 */}
      {showHistoryModal && testcase.previousVersion && (
        <div 
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50"
          onClick={() => setShowHistoryModal(false)}
        >
          <div 
            className="bg-[#1a1a2e] border border-terminal-border rounded-lg p-5 w-full max-w-lg mx-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-terminal-text font-medium flex items-center gap-2">
                <span>📝</span>
                <span>{lang === 'ko' ? '수정 이력' : 'Edit History'}</span>
              </h3>
              <button
                type="button"
                onClick={() => setShowHistoryModal(false)}
                className="text-terminal-muted hover:text-terminal-text transition-colors"
              >
                ✕
              </button>
            </div>
            
            {/* 수정 정보 */}
            <div className="mb-4 flex items-center gap-2 text-xs text-terminal-muted">
              <span className="px-1.5 py-0.5 bg-orange-500/10 border border-orange-500/30 rounded text-orange-400">
                ✏️ {testcase.modifier}
              </span>
              <span>
                {testcase.modifiedAt && new Date(testcase.modifiedAt).toLocaleString(lang === 'ko' ? 'ko-KR' : 'en-US')}
              </span>
            </div>
            
            <div className="space-y-4">
              {/* 변경 전 */}
              <div className="p-3 bg-status-danger/5 border border-status-danger/30 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-status-danger text-xs font-medium">
                    {lang === 'ko' ? '변경 전' : 'Before'}
                  </span>
                </div>
                <div className="space-y-1 text-xs">
                  <p className="text-terminal-text font-medium">{testcase.previousVersion.title}</p>
                  <p className="text-terminal-muted">{testcase.previousVersion.description}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className={`${
                      testcase.previousVersion.priority === 'high' ? 'text-status-danger' :
                      testcase.previousVersion.priority === 'medium' ? 'text-status-warning' :
                      'text-terminal-muted'
                    }`}>
                      {testcase.previousVersion.priority}
                    </span>
                    <span className="text-terminal-dim">|</span>
                    <span className="text-terminal-muted">{testcase.previousVersion.category}</span>
                  </div>
                </div>
              </div>
              
              {/* 화살표 */}
              <div className="flex justify-center">
                <span className="text-terminal-dim text-lg">↓</span>
              </div>
              
              {/* 변경 후 */}
              <div className="p-3 bg-status-go/5 border border-status-go/30 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-status-go text-xs font-medium">
                    {lang === 'ko' ? '변경 후' : 'After'}
                  </span>
                </div>
                <div className="space-y-1 text-xs">
                  <p className={`text-terminal-text font-medium ${testcase.title !== testcase.previousVersion.title ? 'bg-status-go/20 px-1 rounded' : ''}`}>
                    {testcase.title}
                  </p>
                  <p className={`text-terminal-muted ${testcase.description !== testcase.previousVersion.description ? 'bg-status-go/20 px-1 rounded' : ''}`}>
                    {testcase.description}
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className={`${
                      testcase.priority === 'high' ? 'text-status-danger' :
                      testcase.priority === 'medium' ? 'text-status-warning' :
                      'text-terminal-muted'
                    } ${testcase.priority !== testcase.previousVersion.priority ? 'bg-status-go/20 px-1 rounded' : ''}`}>
                      {testcase.priority}
                    </span>
                    <span className="text-terminal-dim">|</span>
                    <span className={`text-terminal-muted ${testcase.category !== testcase.previousVersion.category ? 'bg-status-go/20 px-1 rounded' : ''}`}>
                      {testcase.category}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end mt-4">
              <button
                type="button"
                onClick={() => setShowHistoryModal(false)}
                className="px-4 py-2 text-xs border border-terminal-border text-terminal-muted rounded hover:text-terminal-text transition-colors"
              >
                {lang === 'ko' ? '닫기' : 'Close'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SuggestedTestcasesSection;

